import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { Col, Container, Row } from 'react-bootstrap'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Trans from 'next-translate/Trans'
import { FIXTURES_LIST } from '@graphql/series/fixtures.query'
import { GET_SERIES_NEWS_VIDEOS } from '@graphql/series/home.query'
import queryGraphql from '@shared/components/queryGraphql'
import Layout from '@shared/components/layout'
import { liveArticleListLoader, navLoader, pageHeaderLoader, tableLoader } from '@shared/libs/allLoader'
import { GET_SEO_DATA } from '@graphql/article/article.query'
import { WIDGET } from '@shared-submodule/sm-constants'
import { arraySortByOrder, checkRedirectionStatus, handleApiError } from '@shared-submodule/utils'
import Error from '@shared/components/error'
import { GET_ROUNDS, GET_STANDING_DATA } from '@graphql/series/standings.query'
import { GET_FANTASY_DETAILS } from '@graphql/fantasy-tips/fantasy-tips.query'
import useWindowSize from '@shared/hooks/windowSize'
import { setMatchPlayer } from '@shared/libs/match-detail'
import { GET_SUB_PAGE_BY_SLUG } from '@graphql/seo/seo.query'
import { ENUM_CRICKET_MATCH_PAGES } from '@shared-submodule/sm-enum'
import LazyLoad from '@shared/components/lazyLoad'
import useStatus from '@shared-submodule/sm-hooks/useStatus'
import { useSubscription } from '@apollo/client'
import { GET_MATCH_STATUS } from '@graphql/match/match.subscription'
import StaticBanner from '@shared/components/staticBanner'
import { googleAddData } from '@shared-submodule/sm-constants/googleAdsData'
import WinPercentageWidget from '@shared/components/winPercentageWidget'
import { skyAddData } from '@shared/constants/skyAds'
import { endTime, startTime } from '@shared/libs/metrics'

const GoogleAds = dynamic(() => import('@shared-submodule/sm-components/googleAds'), { ssr: false })
const Skeleton = dynamic(() => import('@shared/components/skeleton'), { ssr: false })
const MatchHeader = dynamic(() => import('@shared/components/match/matchHeader'), { loading: () => pageHeaderLoader() })
const CommonNav = dynamic(() => import('@shared/components/commonNav'), { loading: () => navLoader() })
const AllWidget = dynamic(() => import('@shared/components/allWidget'), { ssr: false })
const TrendingNews = dynamic(() => import('@shared/components/trendingNews'))
const MatchDetailOverview = dynamic(() => import('@shared/components/match/matchDetailOverview'), {
  loading: () => (
    <div className="bg-white rounded p-3">
      {Array.from(Array(5)).map((e, i) => {
        return (
          <React.Fragment key={i}>
            <div className="d-flex">
              <Skeleton width={'10%'} className={'me-3'} />
              <Skeleton width={'25%'} />
            </div>
            <hr />
          </React.Fragment>
        )
      })}
    </div>
  )
})

const MatchDetailScorecard = dynamic(() => import('@shared/components/match/matchDetailScorecard'), { loading: () => tableLoader() })
const MatchDetailFantasyTips = dynamic(() => import('@shared/components/match/matchDetailFantasyTips'), { loading: () => tableLoader() })
const MatchDetailCommentary = dynamic(() => import('@shared/components/match/matchDetailCommentary'), { loading: () => tableLoader() })
// const MatchDetailCommentaryRC = dynamic(() => import('@shared/components/match/matchDetailCommentaryRC'), { loading: () => tableLoader() })
const MatchDetailUpcoming = dynamic(() => import('@shared/components/match/matchDetailUpcoming'), { loading: () => tableLoader() })
const MatchDetailNews = dynamic(() => import('@shared/components/match/matchDetailNews'), { loading: () => tableLoader() })
const MatchDetailOvers = dynamic(() => import('@shared/components/match/matchDetailOvers'), { loading: () => tableLoader() })
const MatchDetailLiveStats = dynamic(() => import('@shared/components/match/matchDetailLiveStats'), { loading: () => tableLoader() })
const MatchDetailStandings = dynamic(() => import('@shared/components/match/matchDetailStandings'))
const LiveArticle = dynamic(() => import('@shared/components/liveArticle'), { loading: () => liveArticleListLoader() })
const NoData = dynamic(() => import('@shared/components/noData'), { ssr: false })
const ATSAds = dynamic(() => import('@shared/components/atsAds'), { ssr: false })
// const Ads = dynamic(() => import('@shared/components/ads'), { ssr: false })

const MatchDetail = props => {
  const {
    matchDetail,
    type,
    scorecard,
    fantasyData,
    commentaryData,
    matchOverView,
    squadData,
    fixturesData,
    newsData,
    recentOverData,
    liveScoreData,
    seoData,
    standingData,
    roundData,
    playingXI,
    liveStatsData,
    inningData,
    liveArticle,
    subPages,
    teamBFormInput,
    teamAFormInput,
    headToHeadData,
    paceSpin,
    recentMatches,
    venueStats
  } = props
  setMatchPlayer(playingXI)
  const { sStatusStr, sLiveGameStatusStr } = useStatus(matchDetail)
  const statusRef = useRef({ sLiveGameStatusStr: sLiveGameStatusStr || sStatusStr, inning: matchDetail?.nLatestInningNumber })
  useSubscription(GET_MATCH_STATUS, {
    variables: { input: { iMatchId: matchDetail?._id } },
    skip: typeof window === 'undefined',
    onData: ({ data }) => {
      const matchDetails = data?.data?.getMatchBySlug?.oMatchDetailsFront
      const currentStatus =
        matchDetails?.oLiveGameStatus?.sValue ||
        matchDetails?.sLiveGameStatusStr ||
        matchDetails?.sStatusStr ||
        matchDetails?.oStatus?.sValue
      const currentInning = matchDetails?.nLatestInningNumber
      if (
        (currentStatus !== statusRef.current.sLiveGameStatusStr && currentStatus) ||
        (currentInning !== statusRef.current?.inning && currentStatus)
      ) {
        router.reload()
      }
    }
  })
  const [width] = useWindowSize()
  const router = useRouter()
  const commentaryOpen = useRef(false)
  const handleCommentary = () => {
    commentaryOpen.current = true
  }

  const is100 = matchDetail?.sFormatStr?.includes('t100')

  const tab = () => {
    // if (type === 'full-scorecard') return (!matchDetail?.bIsCommentary && (scorecard?.fetchLiveInningsData?.length === 0 || scorecard?.fetchLiveInningsData[0]?.aBatters?.length === 0)) ? <NoData /> : <MatchDetailScorecard style={styles} data={scorecard} squad={squadData} matchDetail={matchDetail} matchOverView={matchOverView} />
    if (type === 'full-scorecard') {
      if (
        sStatusStr === 'live' ||
        (scorecard?.fetchLiveInningsData?.length !== 0 && scorecard?.fetchLiveInningsData?.[0]?.aBatters?.length !== 0)
      ) {
        return (
          <MatchDetailScorecard is100={is100} data={scorecard} squad={squadData} matchDetail={matchDetail} matchOverView={matchOverView} />
        )
      } else {
        return <NoData />
      }
    } else if (type === 'fantasy-tips') {
      if (fantasyData?.data?.getFrontFantasyArticle) {
        return <MatchDetailFantasyTips data={fantasyData?.data?.getFrontFantasyArticle} />
      } else {
        return <NoData />
      }
    } else if (type === 'fixtures-and-results') return fixturesData.length !== 0 ? <MatchDetailUpcoming data={fixturesData} /> : <NoData />
    // else if (type === 'fantasy-tips' && (status === 'live' || status === 'completed'))
    // else if (type === 'live-commentary') return (((matchDetail?.bIsCommentary && commentaryData.length !== 0) || liveScoreData?.length !== 0) || commentaryOpen.current === true) ? <MatchDetailCommentary data={commentaryData} matchDetail={matchDetail} liveScoreData={liveScoreData} handleCommentary={handleCommentary} /> : <NoData />
    else if (type === 'overs') {
      return recentOverData?.aResults?.length !== 0 ? (
        <MatchDetailOvers data={recentOverData} matchDetail={matchDetail} is100={is100} />
      ) : (
        <NoData />
      )
    } else if (type === 'live-stats') {
      return liveStatsData.length === 0 ? <NoData title='The numbers are warming up in the nets Stay tuned for all the big hits and stats soon!'/> : <MatchDetailLiveStats inningData={inningData} data={liveStatsData} matchDetail={matchDetail} is100={is100} />
    } else if (type === 'news') {
      return newsData.nTotal === 0 ? (
        <NoData title={<Trans i18nKey="common:NoNewsFound" />} />
      ) : (
        <MatchDetailNews data={newsData} seriesId={matchDetail?.oSeries?._id} />
      )
    } else if (type === 'points-table') {
      return standingData?.length === 0 ? <NoData /> : <MatchDetailStandings roundData={roundData} data={standingData} />
    } else if (type === 'commentary') {
      if (
        (sStatusStr === 'live' || sStatusStr === 'completed' || sStatusStr === 'cancelled') &&
        (commentaryData?.length !== 0 || commentaryOpen.current)
      ) {
        return (
          <MatchDetailCommentary
            data={commentaryData}
            matchDetail={matchDetail}
            liveScoreData={liveScoreData}
            handleCommentary={handleCommentary}
          />
        )
        // return <MatchDetailCommentaryRC data={commentaryData} matchDetail={matchDetail} liveScoreData={liveScoreData} handleCommentary={handleCommentary} />
      } else {
        return <NoData />
      }
    } else if (type === 'live-blog' && matchDetail?.iEventId) {
      return matchDetail?.iEventId ? (
        <div className="common-box py-0">
          <LiveArticle disableMatchCard liveArticleList={liveArticle?.aLiveArticleList} liveEventId={matchDetail?.iEventId} />
        </div>
      ) : (
        <NoData />
      )
    } else {
      if (matchDetail?.sTitle !== null && (matchOverView?.oTeam1 || matchOverView?.oTeam2)) {
        return (
          <MatchDetailOverview
            data={matchOverView}
            matchDetail={matchDetail}
            headToHeadData={headToHeadData}
            teamAFormInput={teamAFormInput}
            teamBFormInput={teamBFormInput}
            paceSpin={paceSpin}
            recentMatches={recentMatches}
            venueStats={venueStats}
          />
        )
      } else {
        return <NoData />
      }
    }
  }

  function fetchTabFromRoute() {
    const str = router?.asPath
    const lastIndex = str.lastIndexOf('/')
    const result = str.substring(lastIndex + 1)
    return result
  }

  function getNavItems() {
    const items = []
    let tabName = fetchTabFromRoute()
    const count = router?.asPath.split('/').length - 1
    if (count === 3) {
      tabName = 'info'
    }
    subPages?.forEach(t => {
      const status = sStatusStr === 'live' ? 'live stats' : 'stats'
      const navitem = t?.eSubType === 'ls' ? status : ENUM_CRICKET_MATCH_PAGES[t?.eSubType]
      const tabActive = t?.eSubType === 'ls' ? 'live-stats' : ENUM_CRICKET_MATCH_PAGES[t?.eSubType]
      if (t?.eSubType !== 'fn' && t?.eSubType !== 'oh' && t?.eSubType !== 'ft') {
        if (t?.eSubType && ENUM_CRICKET_MATCH_PAGES[t?.eSubType]) {
          items.push({
            navItem:
              navitem === 'fixtures-and-results' ? 'fixtures' : navitem === 'full-scorecard' ? 'scorecard' : navitem?.replaceAll(/-/gi, ' '),
            url: `/${t?.sSlug}`,
            active: tabActive === tabName
          })
        } else if (t?.eSubType === null) {
          // Home Tab
          items.push({
            navItem: ENUM_CRICKET_MATCH_PAGES.i,
            url: `/${t?.sSlug}`,
            active: ENUM_CRICKET_MATCH_PAGES.i === tabName
          })
        }
      }
    })
    // if (matchDetail.iEventId) {
    //   items.push({
    //     navItem: ENUM_CRICKET_MATCH_PAGES.lb,
    //     url: `${router.asPath}/${ENUM_CRICKET_MATCH_PAGES.lb}`,
    //     active: ENUM_CRICKET_MATCH_PAGES.lb === tabName
    //   })
    // }
    const order = ['info', 'commentary', 'scorecard', 'stats', 'live stats', 'overs', 'fixtures', 'news', 'points table', 'fantasy tips']
    return arraySortByOrder({ data: items, order, key: 'navItem' })
  }

  function changeSeoData(data) {
    return {
      ...data,
      sTitle: makeTitle(matchDetail),
      sDescription: makeDescription(matchDetail),
      sRobots: getRobots(matchDetail, data)
    }
  }

  function getRobots(match, seo) {
    // if (type === 'news' || type === 'fantasy-tips' || type === 'points-table') return 'Noindex, Follow'
    if (type === 'live-blog') return 'Noindex, Follow'
    return seo?.sRobots
  }

  function makeTitle(match) {
    const seriesName = `${match?.oSeries?.sTitle} ${match?.oSeries?.sSeason}`
    const matchShortName = `${match?.oTeamScoreA?.oTeam?.sAbbr} vs ${match?.oTeamScoreB?.oTeam?.sAbbr}`
    const matchFullName = `${match?.oTeamScoreA?.oTeam?.sTitle} vs ${match?.oTeamScoreB?.oTeam?.sTitle}`

    if (typeof document !== 'undefined' && document.title !== '' && sStatusStr === 'live') {
      return document.title
    } else if (type === 'full-scorecard') {
      return `Cricket scorecard - ${matchFullName} ${match?.sSubtitle}, ${seriesName}`
    } else if (type === 'news') {
      return `Cricket news ${matchFullName} ${match?.sSubtitle}, ${seriesName}`
    } else if (type === 'overs') {
      return `${matchShortName} Overs, ${matchFullName}, ${seriesName}`
    } else if (type === 'fixtures-and-results') {
      return `${seriesName} Results and Fixtures`
    } else if (type === 'commentary') {
      return `Get Ball by Ball Commentary of ${matchFullName} ${match?.sSubtitle}`
    } else {
      // return `${seriesName} | ${matchFullName} Live cricket Score & Updates`
      return `Live Score & Latest Updates of ${matchShortName}`
    }
  }

  function makeDescription(match) {
    const matchFullName = `${match?.oTeamScoreA?.oTeam?.sTitle} vs ${match?.oTeamScoreB?.oTeam?.sTitle}`
    const matchName = `${match?.oTeamScoreA?.oTeam?.sAbbr} vs ${match?.oTeamScoreB?.oTeam?.sAbbr}`
    const seriesName = `${match?.oSeries?.sTitle} ${match?.oSeries?.sSeason}`

    if (type === 'full-scorecard') {
      return `Find the complete scorecard of ${matchFullName}, ${match?.sSubtitle}, ${seriesName}`
    } else if (type === 'news') {
      return `Get related news of ${matchFullName}`
    } else if (type === 'overs') {
      return `${matchName} Overs, ${matchFullName}, ${match?.sSubtitle}, ${seriesName}`
    } else if (type === 'fixtures-and-results') {
      return `${seriesName} Get all Results and Fixtures matches`
    } else if (type === 'commentary') {
      return `Check full commentary of ${matchFullName} ${match?.sSubtitle}`
    } else {
      return `Get Live Score , commentry and latest updates of ${matchName} only on OneTurf`
    }
  }

  return (
    <Layout data={{ ...matchDetail, oSeo: changeSeoData(seoData) }} matchDetail={{ ...matchDetail, commentary: commentaryData }}>
      <main className="common-section pt-4">
        <ATSAds />
        <Container>
          {/* <div className="d-none d-md-block mb-3" style={{ minHeight: '90px', marginTop: '-15px' }}>
            {width > 767 && ( // Desktop top
              <Ads
                id="div-ad-gpt-138639789--0-OneTurf2022_Desktop_Top_970"
                adIdDesktop="OneTurf2022_Desktop_Top_970x90"
                dimensionDesktop={[970, 90]}
                className={'text-center'}
                style={{ minHeight: '90px' }}
              />
            )}
          </div> */}
          <Row>
            <Col lg={9} className="left-content">
              <MatchHeader data={matchDetail} is100={is100} />
              {/* <StaticBannerSecond adType="secondAd_long" link="match_detail_top" /> */}
              <div className="w-full d-none d-xl-flex justify-content-center ">
                <GoogleAds
                  addId={googleAddData?.addTop?.addId}
                  divId={googleAddData?.addTop?.divId}
                  dimension={googleAddData?.addTop?.dimension}
                  slotId={googleAddData?.addTop?.slotId}
                  className={'mt-4 mb-0'}
                />
              </div>
              <div className="w-full d-flex d-xl-none justify-content-center mt-3">
                <GoogleAds
                  addId={googleAddData?.mobSm?.addId}
                  divId={googleAddData?.mobSm?.divId}
                  slotId={googleAddData?.mobSm?.slotId}
                  dimension={googleAddData?.mobSm?.dimension}
                  className={'mt-4'}
                />
              </div>
              <CommonNav items={getNavItems()} isMatch />
              {/* <div className="mb-3 d-flex">
                <Ads
                  id="div-ad-gpt-138639789-1660147282-1"
                  adIdDesktop="OneTurf2022_Desktop_LiveScore_ATF_728x90"
                  adIdMobile="OneTurf2022_Mobile_LiveScore_ATF_300x250"
                  dimensionDesktop={[728, 90]}
                  dimensionMobile={[300, 250]}
                  mobile
                  className={'text-center w-100'}
                />
              </div> */}
              {tab()}
              {width > 1400 && (
                <GoogleAds
                  addId={skyAddData?.addBottom?.addId}
                  divId={skyAddData?.addBottom?.divId}
                  dimension={skyAddData?.addBottom?.dimension}
                  slotId={skyAddData?.addBottom?.slotId}
                  className={'my-3'}
                />
              )}
              {width < 720 && (
                <GoogleAds
                  addId={skyAddData?.addRight?.addId}
                  divId={skyAddData?.addRight?.divId}
                  dimension={skyAddData?.addRight?.dimension}
                  slotId={skyAddData?.addRight?.slotId}
                  className={'my-3'}
                />
              )}
            </Col>
            {width > 768 ? (
              <Col lg={3} className="common-sidebar">
                <TrendingNews />
                <ATSAds />
                <StaticBanner isLeft />
                {(matchDetail?.sStatusStr === 'scheduled' || matchDetail?.sStatusStr === 'live') && (
                  <WinPercentageWidget data={matchDetail?.oWinPercent} />
                )}
                <LazyLoad>
                  <AllWidget type={WIDGET.prioritySeries} show />
                  <AllWidget type={WIDGET.upcomingMatches} show />
                  <GoogleAds
                    addId={googleAddData?.addRight?.addId}
                    divId={googleAddData?.addRight?.divId}
                    dimension={googleAddData?.addRight?.dimension}
                    slotId={googleAddData?.addRight?.slotId}
                    className={'mb-4'}
                  />
                  <AllWidget type={WIDGET.ranking} show />
                </LazyLoad>
                {/* <StaticBannerSecond adType="secondAd_Rect" link="match_detail_right" /> */}
                {/* {width > 991 && (
                    <Ads
                      id="div-ad-gpt-138639789-1660147282-0"
                      adIdDesktop="OneTurf2022_Desktop_LiveScore_RightATF_300x600"
                      dimensionDesktop={[300, 600]}
                      className="text-center sticky-ads"
                    />
                  )} */}
              </Col>
            ) : (
              <>
                <StaticBanner isLeft />
                {/* <StaticBannerSecond adType="secondAd_Rect" link="match_detail_right" /> */}
              </>
            )}
          </Row>
        </Container>
      </main>
    </Layout>
  )
}

MatchDetail.propTypes = {
  type: PropTypes.string,
  matchDetail: PropTypes.object,
  scorecard: PropTypes.object,
  error: PropTypes.any,
  fantasyData: PropTypes.object,
  status: PropTypes.string,
  fantasyPlatFormData: PropTypes.object,
  matchId: PropTypes.string,
  overViewData: PropTypes.object,
  commentaryData: PropTypes.array,
  liveStatsData: PropTypes.array,
  inningData: PropTypes.array,
  matchOverView: PropTypes.object,
  squadData: PropTypes.object,
  fixturesData: PropTypes.array,
  newsData: PropTypes.object,
  recentOverData: PropTypes.object,
  liveScoreData: PropTypes.array,
  seoData: PropTypes.object,
  standingData: PropTypes.array,
  roundData: PropTypes.array,
  handleCommentary: PropTypes.func,
  seriesTotalTeams: PropTypes.number,
  playingXI: PropTypes.object,
  subPages: PropTypes.array,
  liveArticle: PropTypes.shape({ aLiveArticleList: PropTypes.array }),
  headToHeadData: PropTypes.array,
  paceSpin: PropTypes.array,
  venueStats: PropTypes.array,
  recentMatches: PropTypes.array,
  teamAFormInput: PropTypes.array,
  teamBFormInput: PropTypes.array
}

export default Error(MatchDetail)

export async function getServerSideProps({ res, resolvedUrl }) {
  const slug = resolvedUrl
    .split('?')[0]
    .split('/')
    .filter(x => x)
  const currentPage = slug[slug.length - 1]
  const mSlug = slug.join('/')
  const {
    FULL_SCOREBOARD,
    MATCH_DETAIL,
    GET_COMMENTARY,
    GET_MATCH_OVERVIEW,
    GET_MATCH_SQUAD,
    RECENT_OVER,
    LIVE_INNING_DATA,
    FANTASY_TIPS_DETAILS,
    GET_MATCH_PLAYING_XI,
    GET_MATCH_STATS,
    GET_HEAD_TO_HEAD,
    GET_PACE_SPIN,
    GET_RECENT_MATCHES,
    GET_VENUE_STATS,
    GET_TEAM_FORM
  } = await import('@graphql/match/match.query')
  const parallelCalls = (await import('@shared-submodule/utils')).parallelCalls
  try {
    res.setHeader('Cache-Control', 'public, max-age=7')
    const { data: seoData } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: mSlug } })
    // Check Redirection
    const { redirectStatus, eCode, returnObj, props } = checkRedirectionStatus(seoData?.getSeoData)
    if (redirectStatus && props) {
      res.statusCode = eCode
      return { props }
    } else if (redirectStatus) return returnObj

    startTime('GET_MATCH_DETAILS_DATA')
    const [match, playingXI, subPages] = await parallelCalls([
      queryGraphql(MATCH_DETAIL, { input: { _id: seoData?.getSeoData?.iId } }),
      queryGraphql(GET_MATCH_PLAYING_XI, { input: { iMatchId: seoData?.getSeoData?.iId } }),
      queryGraphql(GET_SUB_PAGE_BY_SLUG, { input: { sSlug: mSlug } })
    ])

    if (slug.length > 3) {
      if (currentPage === 'full-scorecard') {
        const scorecardQuery = [
          queryGraphql(FULL_SCOREBOARD, { input: { iMatchId: match?.getMatchById?._id } }),
          queryGraphql(GET_MATCH_SQUAD, { input: { iMatchId: match?.getMatchById?._id } })
        ]

        if (match?.getMatchById?.oStatus?.sValue === 'completed' || match?.getMatchById?.oStatus?.sValue === 'live') {
          scorecardQuery.push(queryGraphql(GET_MATCH_OVERVIEW, { input: { iMatchId: match?.getMatchById?._id } }))
          const [data, squadData, overviewInfo] = await parallelCalls(scorecardQuery)
          return {
            props: {
              type: currentPage,
              matchDetail: match?.getMatchById || {},
              matchOverView: overviewInfo?.getMatchOverviewFront || {},
              scorecard: data || {},
              squadData: squadData || {},
              seoData: seoData?.getSeoData || {},
              playingXI: playingXI?.getMatchOverviewFront || {},
              subPages: subPages?.getSeosBySlug || {}
            }
          }
        }
        const [data, squadData] = await parallelCalls(scorecardQuery)
        const timeTaken = endTime('GET_MATCH_DETAILS_DATA')
        return {
          props: {
            perf: timeTaken,
            type: currentPage,
            matchDetail: match?.getMatchById || {},
            scorecard: data || {},
            squadData: squadData || {},
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'fantasy-tips') {
        const { data } = await queryGraphql(FANTASY_TIPS_DETAILS, { input: { iMatchId: match?.getMatchById?._id } })
        if (data?.getOverviewFront && data?.getOverviewFront?.aCricPrediction?.length > 0) {
          const fantasydata = await queryGraphql(GET_FANTASY_DETAILS, { input: { _id: data?.getOverviewFront?.aCricPrediction?.[0]?._id } })
          return {
            props: {
              type: currentPage,
              matchDetail: match?.getMatchById || {},
              status: match?.getMatchById?.oStatus?.sValue || {},
              fantasyData: fantasydata || {},
              seoData: seoData?.getSeoData || {},
              playingXI: playingXI?.getMatchOverviewFront || {},
              subPages: subPages?.getSeosBySlug || {}
            }
          }
        } else {
          const timeTaken = endTime('GET_MATCH_DETAILS_DATA')
          return {
            props: {
              perf: timeTaken,
              type: currentPage,
              matchDetail: match?.getMatchById || {},
              status: match?.getMatchById?.oStatus?.sValue || {},
              fantasyData: data || {},
              seoData: seoData?.getSeoData || {},
              playingXI: playingXI?.getMatchOverviewFront || {},
              subPages: subPages?.getSeosBySlug || {}
            }
          }
        }
      } else if (currentPage === 'fixtures-and-results') {
        const { data } = await queryGraphql(FIXTURES_LIST, {
          input: { iSeriesId: match?.getMatchById?.oSeries?._id, nOrder: 1, sSortBy: 'dStartDate' }
        })
        const timeTaken = endTime('GET_MATCH_DETAILS_DATA')
        return {
          props: {
            perf: timeTaken,
            type: currentPage,
            matchDetail: match?.getMatchById || {},
            fixturesData: data?.fetchFixuresData || {},
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'news') {
        const { data } = await queryGraphql(GET_SERIES_NEWS_VIDEOS, {
          input: { iSeriesId: match?.getMatchById?.oSeries?._id, eType: 'n', nLimit: 7, nSkip: 1 }
        })
        return {
          props: {
            type: currentPage,
            matchDetail: match?.getMatchById || {},
            newsData: data?.listSeriesArticlesVideosFront?.oArticles || {},
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'overs') {
        const { data } = await queryGraphql(RECENT_OVER, { input: { iMatchId: match?.getMatchById?._id, nLimit: 5, nSkip: 0 } })
        return {
          props: {
            type: currentPage,
            matchDetail: match?.getMatchById || {},
            recentOverData: data?.listMatchOvers || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            seoData: seoData?.getSeoData || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'live-stats') {
        const { data } = await queryGraphql(GET_MATCH_STATS, { input: { iMatchId: match?.getMatchById?._id } })
        const inningData = await queryGraphql(FULL_SCOREBOARD, { input: { iMatchId: match?.getMatchById?._id } })
        return {
          props: {
            type: currentPage || {},
            liveStatsData: data?.getMatchStatistics || {},
            matchDetail: match?.getMatchById || {},
            seoData: seoData?.getSeoData || {},
            inningData: inningData?.data?.fetchLiveInningsData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'live-blog') {
        const liveArticle = {}
        if (match?.getMatchById?.iEventId) {
          const { getLiveArticleData } = await import('@shared-submodule/sm-libs/live-blog')
          const { aLiveArticleList } = await getLiveArticleData({ iEventId: match.getMatchById.iEventId })
          liveArticle.aLiveArticleList = aLiveArticleList.length ? aLiveArticleList : []
        }
        return {
          props: {
            type: currentPage,
            liveArticle: liveArticle || {},
            matchDetail: match?.getMatchById || {},
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'commentary') {
        // For commentary
        const [data, liveScoreData] = await parallelCalls([
          // queryGraphql(GET_COMMENTARY, { input: { iMatchId: match?.getMatchById?._id, nLimit: 15, nInningNumber: match?.getMatchById?.nLatestInningNumber } }),
          queryGraphql(GET_COMMENTARY, { fetchPolicy: 'network-only', input: { iMatchId: match?.getMatchById?._id, nLimit: 15 } }),
          queryGraphql(LIVE_INNING_DATA, {
            input: { iMatchId: match?.getMatchById?._id, nInningNumber: match?.getMatchById?.nLatestInningNumber }
          })
        ])
        return {
          props: {
            type: slug[slug.length - 1],
            commentaryData: data?.listMatchCommentaries || {},
            // commentaryData: data?.listFinalMatchCommentaries || [],
            matchDetail: match?.getMatchById || {},
            liveScoreData: liveScoreData?.fetchLiveInningsData || {},
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      } else if (currentPage === 'points-table') {
        const [roundData, standingData] = await parallelCalls([
          queryGraphql(GET_ROUNDS, { input: { iSeriesId: match?.getMatchById?.oSeries?._id } }),
          queryGraphql(GET_STANDING_DATA, { input: { iRoundId: null, iSeriesId: match?.getMatchById?.oSeries?._id } })
        ])
        return {
          props: {
            type: currentPage,
            matchDetail: match?.getMatchById || {},
            roundData: roundData?.fetchSeriesRounds || [],
            standingData: standingData?.fetchSeriesStandings || [],
            seoData: seoData?.getSeoData || {},
            playingXI: playingXI?.getMatchOverviewFront || {},
            subPages: subPages?.getSeosBySlug || {}
          }
        }
      }
      return { props: { error: JSON.stringify(seoData) } }
    } else {
      // For overview
      const [data, headToHeadData, teamAFormInput, teamBFormInput, paceSpin, recentMatches, venueStats] = await parallelCalls([
        queryGraphql(GET_MATCH_OVERVIEW, { input: { iMatchId: match?.getMatchById?._id } }),
        queryGraphql(GET_HEAD_TO_HEAD, {
          input: { iTeamIds: [match?.getMatchById?.oTeamScoreA?.oTeam?._id, match?.getMatchById?.oTeamScoreB?.oTeam?._id] }
        }),
        queryGraphql(GET_TEAM_FORM, { input: { iTeamId: match?.getMatchById?.oTeamScoreA?.oTeam?._id, nLimit: 5 } }),
        queryGraphql(GET_TEAM_FORM, { input: { iTeamId: match?.getMatchById?.oTeamScoreB?.oTeam?._id, nLimit: 5 } }),
        queryGraphql(GET_PACE_SPIN, { input: { iMatchId: match?.getMatchById?._id } }),
        queryGraphql(GET_RECENT_MATCHES, { input: { iMatchId: match?.getMatchById?._id } }),
        queryGraphql(GET_VENUE_STATS, { input: { iMatchId: match?.getMatchById?._id } })
      ])
      const timeTaken = endTime('GET_MATCH_DETAILS_DATA')
      return {
        props: {
          perf: timeTaken,
          matchDetail: match?.getMatchById || {},
          matchOverView: data?.getMatchOverviewFront || {},
          seoData: seoData?.getSeoData || {},
          playingXI: playingXI?.getMatchOverviewFront || {},
          subPages: subPages?.getSeosBySlug || {},
          headToHeadData: headToHeadData?.getHeadToHead || {},
          teamAFormInput: teamAFormInput?.getTeamForm || {},
          teamBFormInput: teamBFormInput?.getTeamForm || {},
          paceSpin: paceSpin?.getVenueStatsByMatchId || {},
          recentMatches: recentMatches?.getVenueStatsByMatchId || {},
          venueStats: venueStats?.getVenueStatsByMatchId || {}
        }
      }
    }
  } catch (e) {
    res.setHeader('Cache-Control', 'no-cache')
    const status = handleApiError(e, resolvedUrl)
    return status
  }
}
